import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import DateTimePicker from '../../components/ui/DateTimePicker';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import { useAuth } from '../../context/AuthContext';
import { Grid, PageEmpty, PageWrap } from '../performance-operations/styles';
import { fetchPeriods, usePanel } from '../performance-operations/config';
import {
  DevelopmentAttentionPanel, DevelopmentKpis, DueCalendarPanel, PlanStatusPanel,
} from './components/DevelopmentPanels';
import RequirementsPanel from './components/RequirementsPanel';
import PlanListTable from './components/PlanListTable';
import { PipAttentionPanel, PipKpis, PipStatusPanel } from './components/PipPanels';
import PipListTable from './components/PipListTable';
import { FilterField, FilterRow, Note } from './styles';
import {
  Q_TAB, TAB_IDP, TAB_PIP, fetchDevelopmentSummary, fetchPipSummary,
} from './config';

// PM analitik P5 — E12 "Gelişim Planları ve PIP" (/performance/development-analytics; analiz §15). Yönetici ve İK için gelişim
// aksiyonlarını ve onaylanmış iyileştirme planlarını izler. Gelişim planı ile PIP ayrı sekmeler: gelişim planı TD modülü açıksa
// (İK + yönetici; yönetici kendi kişi kümesini görür), PIP yalnız İK (PIP okuma kapsamı İK'dır). Ortak süzgeç şeridi URL'de;
// panel başına bağımsız yükleme/hata/Tekrar Dene; grafiklerin tablo alternatifi; grafik/dikkat tıklaması listeye süzgeç ekler.
// AI kararı yok: PIP başlatma, kapanış sonucu ya da çalışma ilişkisi kararı bu ekrandan verilmez (insan onaylı mevcut akış).
export default function PerformanceDevelopmentAnalyticsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr, hasModule } = useAuth();
  const tdOn = hasModule('TD');
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [planFilters, setPlanFilters] = useState({ statuses: [], categories: [], overdue: false, required: false });
  const [pipStatuses, setPipStatuses] = useState([]);
  const [closeWindow, setCloseWindow] = useState({ from: null, to: null });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const tabs = useMemo(() => [
    ...(tdOn ? [{ key: TAB_IDP, label: t('performance.developmentAnalytics.tab.idp') }] : []),
    ...(isHr ? [{ key: TAB_PIP, label: t('performance.developmentAnalytics.tab.pip') }] : []),
  ], [tdOn, isHr, t]);
  const requested = new URLSearchParams(location.search).get(Q_TAB);
  const activeTab = tabs.some((x) => x.key === requested) ? requested : tabs[0]?.key;
  const setTab = useCallback((key) => {
    const p = new URLSearchParams(location.search);
    p.set(Q_TAB, key);
    history.replace({ pathname: location.pathname, search: `?${p.toString()}` });
  }, [history, location.pathname, location.search]);

  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);
  const key = JSON.stringify(filters);
  const devSummary = usePanel(() => fetchDevelopmentSummary(filters), [key, refreshKey], ready && activeTab === TAB_IDP);
  const pipSummary = usePanel(() => fetchPipSummary(filters, closeWindow), [key, JSON.stringify(closeWindow)], ready && activeTab === TAB_PIP);

  const pickPlanStatus = useCallback((s) => setPlanFilters((f) => ({ ...f, statuses: [s] })), []);
  const pickOverdue = useCallback(() => setPlanFilters((f) => ({ ...f, overdue: true })), []);
  const pickPipStatus = useCallback((s) => setPipStatuses([s]), []);
  const onRequirementsChanged = useCallback(() => setRefreshKey((k) => k + 1), []);

  const lastUpdated = activeTab === TAB_PIP ? pipSummary.data?.calculatedAt : devSummary.data?.calculatedAt;

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t('performance.developmentAnalytics.title') }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.operations.noPeriods')}</PageEmpty> : null}
      {tabs.length === 0 ? <PageEmpty data-state="no-access">{t('performance.developmentAnalytics.noAccess')}</PageEmpty> : null}
      {periods.length > 0 && tabs.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={lastUpdated || null}
          stale={false}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready && tabs.length > 0 ? (
        <>
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setTab} />
          {activeTab === TAB_IDP ? (
            <>
              <Note>{t('performance.developmentAnalytics.idpScopeNote')}</Note>
              <Grid key={`idp-${filters.periodId}`}>
                <DevelopmentKpis panel={devSummary} />
                <PlanStatusPanel panel={devSummary} onPickStatus={pickPlanStatus} />
                <DueCalendarPanel panel={devSummary} onPickOverdue={pickOverdue} />
                <DevelopmentAttentionPanel panel={devSummary} onPickStatus={pickPlanStatus} onPickOverdue={pickOverdue} />
                <RequirementsPanel filters={filters} isHr={isHr} onChanged={onRequirementsChanged} />
                <PlanListTable filters={filters} planFilters={planFilters} onPlanFilters={setPlanFilters} refreshKey={refreshKey} />
              </Grid>
            </>
          ) : null}
          {activeTab === TAB_PIP ? (
            <>
              <FilterRow>
                <FilterField>
                  <DateTimePicker
                    label={t('performance.developmentAnalytics.pip.closedFrom')}
                    value={closeWindow.from}
                    onChange={(v) => setCloseWindow((w) => ({ ...w, from: v }))}
                  />
                </FilterField>
                <FilterField>
                  <DateTimePicker
                    label={t('performance.developmentAnalytics.pip.closedTo')}
                    value={closeWindow.to}
                    onChange={(v) => setCloseWindow((w) => ({ ...w, to: v }))}
                  />
                </FilterField>
              </FilterRow>
              <Grid key={`pip-${filters.periodId}`}>
                <PipKpis panel={pipSummary} />
                <PipStatusPanel panel={pipSummary} onPickStatus={pickPipStatus} />
                <PipAttentionPanel panel={pipSummary} onPickStatus={pickPipStatus} />
                <PipListTable filters={filters} closeWindow={closeWindow} statuses={pipStatuses} onStatuses={setPipStatuses} />
              </Grid>
            </>
          ) : null}
        </>
      ) : null}
    </PageWrap>
  );
}
